import React, { useState } from "react";
import { Box, Typography, Button, IconButton, Popover } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { getStartOfWeek } from "../utils/dateUtils";

const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const toLocalStr = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const isToday = (s) => toLocalStr(new Date()) === s;

/**
 * Jira-style week picker popover. Selecting any day returns the Sunday that
 * starts that week via onChange(Date). Shared by TimesheetV2 and the
 * Timesheet Download/Approval history navigator.
 */
export default function WeekPicker({ weekStart, onChange, anchorEl, onClose }) {
  const [view, setView] = useState(new Date(weekStart));
  const firstOfMonth = new Date(view.getFullYear(), view.getMonth(), 1);
  const startPad     = firstOfMonth.getDay();
  const daysInMonth  = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
  const cells = [...Array(startPad).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(view.getFullYear(), view.getMonth(), i + 1))];
  const ws = getStartOfWeek(weekStart);
  const we = addDays(ws, 6);
  const inSel = (d) => d && d >= ws && d <= we;
  const isWS  = (d) => d && toLocalStr(d) === toLocalStr(ws);
  const isWE  = (d) => d && toLocalStr(d) === toLocalStr(we);

  return (
    <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{ sx: { borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.18)", minWidth: 280, overflow: "hidden" } }}>
      <Box sx={{ bgcolor: "#0052CC", color: "#fff", px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <IconButton size="small" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} sx={{ color: "#fff" }}>
          <ArrowBackIos sx={{ fontSize: 13 }} />
        </IconButton>
        <Typography fontWeight={700} fontSize="0.9rem">{MONTHS[view.getMonth()]} {view.getFullYear()}</Typography>
        <IconButton size="small" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} sx={{ color: "#fff" }}>
          <ArrowForwardIos sx={{ fontSize: 13 }} />
        </IconButton>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", px: 1, pt: 1, bgcolor: "#fff" }}>
        {WEEKDAYS_SHORT.map((d) => (
          <Typography key={d} variant="caption" align="center"
            sx={{ color: "#5E6C84", fontWeight: 700, fontSize: "0.65rem", pb: 0.5 }}>{d}</Typography>
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", px: 1, pb: 1.5, bgcolor: "#fff" }}>
        {cells.map((d, i) => {
          const sel = inSel(d), start = isWS(d), end = isWE(d), tod = d && isToday(toLocalStr(d));
          return (
            <Box key={i} onClick={() => { if (d) { onChange(getStartOfWeek(d)); onClose(); } }}
              sx={{
                height: 32, display: "flex", alignItems: "center", justifyContent: "center",
                cursor: d ? "pointer" : "default",
                bgcolor: sel ? "#0052CC" : "transparent",
                borderRadius: start ? "50% 0 0 50%" : end ? "0 50% 50% 0" : 0,
                ...(start && end && { borderRadius: "50%" }),
                "&:hover": d ? {
                  bgcolor: sel ? "#0065FF" : "#EBECF0",
                  borderRadius: start ? "50% 0 0 50%" : end ? "0 50% 50% 0" : sel ? 0 : "4px",
                } : {},
              }}>
              {d && (
                <Typography variant="caption" sx={{
                  fontWeight: tod ? 800 : sel ? 600 : 400,
                  color: sel ? "#fff" : tod ? "#0052CC" : "#172B4D",
                  fontSize: "0.8rem", lineHeight: 1,
                  border: tod && !sel ? "2px solid #0052CC" : "none",
                  borderRadius: "50%", width: 22, height: 22,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>{d.getDate()}</Typography>
              )}
            </Box>
          );
        })}
      </Box>
      <Box sx={{ borderTop: "1px solid #EBECF0", px: 2, py: 1, display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#fff" }}>
        <Button size="small" sx={{ textTransform: "none", fontSize: "0.75rem", color: "#0052CC" }}
          onClick={() => { onChange(getStartOfWeek(new Date())); onClose(); }}>This week</Button>
        <Typography variant="caption" color="text.secondary">Week of {toLocalStr(ws)}</Typography>
      </Box>
    </Popover>
  );
}
